/**
 * Bulk actions on the register. Bulk is many singles — so the UI's job is to show the
 * count it will act on BEFORE, and every per-item outcome AFTER. There is no "done"
 * toast: a bulk close-out over forty documents where six were blocked is not "done".
 */
import { useCallback, useState } from 'react';
import { Download, FileSpreadsheet, Loader2, Send, Stamp, Tags, X } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';

const EDITABLE = ['discipline', 'doc_type', 'wbs', 'custodian', 'distribution', 'originator'];

export default function EdmBulkBar({ projectId, selected, clear, filters, matchingCount,
  allMatching, setAllMatching, onDone }) {
  const T = useNfaTheme();
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [result, setResult] = useState(null);
  const count = allMatching ? matchingCount : selected.size;

  const body = useCallback((extra = {}) => ({
    documentIds: allMatching ? [] : [...selected],
    filters,
    allMatchingFilter: allMatching,
    ...extra,
  }), [allMatching, selected, filters]);

  const run = async (path, extra, { download } = {}) => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/edm/bulk/${path}?project_id=${projectId}`, body(extra),
        download ? { responseType: 'blob' } : undefined);
      if (download) {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = download; a.click(); URL.revokeObjectURL(url);
        setResult({ note: `${download} downloaded. `
          + (r.headers['x-edm-watermarked'] ? `${r.headers['x-edm-watermarked']} superseded file(s) were watermarked on the served copy; the stored files are unaltered.` : '') });
      } else {
        setResult(r.data);
        onDone && onDone();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setResult({ error: typeof d === 'string' ? d : (d?.error || 'The operation was refused') });
    } finally { setBusy(false); }
  };

  // The outcome report must outlive the action. Clearing the selection on success
  // unmounted the bar and took the per-item outcomes with it — which is exactly the
  // information the user needs after a bulk operation, and the opposite of a toast.
  if (!count && !result) return null;
  const rows = result?.results || result?.blockedItems || [];

  return (
    <div className="rounded-lg p-3 space-y-2" data-testid="edm-bulk-bar"
      style={{ background: T.surface, border: `1px solid ${T.primary}55` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11.5px] font-medium" style={{ color: T.ink }}
          data-testid="edm-bulk-count">
          {count ? `${count} document${count === 1 ? '' : 's'} selected`
            : 'Last bulk operation — selection cleared, the outcomes stay below'}
          {count && allMatching ? ' — every document matching the current filter' : ''}
        </span>
        {!allMatching && matchingCount > selected.size && (
          <button onClick={() => setAllMatching(true)} data-testid="edm-bulk-select-all-matching"
            className="text-[11px] underline" style={{ color: T.primary }}>
            select all {matchingCount} matching this filter
          </button>
        )}
        <button onClick={() => { clear(); setAllMatching(false); setResult(null); setOpen(null); }}
          data-testid="edm-bulk-clear" className="text-[11px] ml-auto inline-flex items-center gap-1"
          style={{ color: T.muted }}><X className="w-3 h-3" /> clear</button>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {[['transmittal', 'Transmittal', Send], ['zip', 'Download zip', Download],
          ['metadata', 'Edit metadata', Tags], ['csv', 'Export CSV', FileSpreadsheet],
          ['status', 'Stamp status', Stamp]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => { setOpen(open === key ? null : key); setResult(null); }}
            data-testid={`edm-bulk-${key}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px]"
            style={{ border: `1px solid ${open === key ? T.primary : T.border}`,
              color: open === key ? T.primary : T.body }}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
        {busy && <Loader2 className="w-4 h-4 animate-spin" style={{ color: T.primary }} />}
      </div>

      {open === 'transmittal' && (
        <div className="flex items-end gap-2 flex-wrap" data-testid="edm-bulk-transmittal-form">
          <input placeholder="recipient organisation" value={form.toOrg || ''}
            data-testid="edm-bulk-transmittal-org"
            onChange={(e) => setForm({ ...form, toOrg: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px] w-56"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <input placeholder="purpose (e.g. For construction)" value={form.purpose || ''}
            data-testid="edm-bulk-transmittal-purpose"
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px] w-56"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <button disabled={!form.toOrg || busy} data-testid="edm-bulk-transmittal-create"
            onClick={() => run('transmittal', { toOrg: form.toOrg, purpose: form.purpose || 'For construction' })}
            className="px-3 py-1.5 rounded text-[11px] font-medium disabled:opacity-40"
            style={{ background: T.primary, color: '#fff' }}>
            One transmittal, {count} item{count === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {open === 'zip' && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="edm-bulk-zip-form">
          <span className="text-[11px]" style={{ color: T.muted }}>
            Superseded revisions are watermarked on the copy in the archive. The stored files
            are not touched and MANIFEST.csv quotes the hash of the stored bytes.
          </span>
          <button disabled={busy} data-testid="edm-bulk-zip-download"
            onClick={() => run('zip', {}, { download: 'edm-documents.zip' })}
            className="px-3 py-1.5 rounded text-[11px] font-medium"
            style={{ background: T.primary, color: '#fff' }}>Download</button>
        </div>
      )}

      {open === 'metadata' && (
        <div className="flex items-end gap-2 flex-wrap" data-testid="edm-bulk-metadata-form">
          <select value={form.field || ''} data-testid="edm-bulk-metadata-field"
            onChange={(e) => setForm({ ...form, field: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px]"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }}>
            <option value="">field…</option>
            {EDITABLE.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <input placeholder="new value" value={form.value || ''}
            data-testid="edm-bulk-metadata-value"
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px] w-56"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <button disabled={!form.field || busy} data-testid="edm-bulk-metadata-preview"
            onClick={() => run('metadata/preview', { changes: { [form.field]: form.value } })}
            className="px-3 py-1.5 rounded text-[11px]"
            style={{ border: `1px solid ${T.border}`, color: T.body }}>Preview the change</button>
          <button disabled={!form.field || busy || !result?.fields}
            data-testid="edm-bulk-metadata-apply"
            onClick={() => run('metadata/apply', { changes: { [form.field]: form.value } })}
            className="px-3 py-1.5 rounded text-[11px] font-medium disabled:opacity-40"
            style={{ background: T.primary, color: '#fff' }}>Apply</button>
        </div>
      )}

      {open === 'csv' && (
        <button disabled={busy} data-testid="edm-bulk-csv-download"
          onClick={() => run('csv', {}, { download: 'edm-register.csv' })}
          className="px-3 py-1.5 rounded text-[11px] font-medium"
          style={{ background: T.primary, color: '#fff' }}>
          Export {count} row{count === 1 ? '' : 's'} as filtered
        </button>
      )}

      {open === 'status' && (
        <div className="flex items-end gap-2 flex-wrap" data-testid="edm-bulk-status-form">
          <input placeholder="status code (e.g. 2)" value={form.statusCode || ''}
            data-testid="edm-bulk-status-code"
            onChange={(e) => setForm({ ...form, statusCode: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px] w-32"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <input placeholder="recommended by (name)" value={form.byName || ''}
            data-testid="edm-bulk-status-by"
            onChange={(e) => setForm({ ...form, byName: e.target.value })}
            className="px-2 py-1.5 rounded text-[11px] w-48"
            style={{ background: T.page, border: `1px solid ${T.border}`, color: T.ink }} />
          <span className="text-[11px]" style={{ color: T.muted }}>
            Every item goes through the same gate as a single close-out. Blocked documents are
            reported, never forced.
          </span>
          <button disabled={!form.statusCode || busy} data-testid="edm-bulk-status-apply"
            onClick={() => run('status', { statusCode: form.statusCode,
              codeRecommendedBy: form.byName ? { name: form.byName } : null })}
            className="px-3 py-1.5 rounded text-[11px] font-medium disabled:opacity-40"
            style={{ background: T.primary, color: '#fff' }}>Stamp {count}</button>
        </div>
      )}

      {result && (
        <div className="rounded p-2 text-[11px] space-y-1" data-testid="edm-bulk-result"
          style={{ background: T.page, border: `1px solid ${T.border}` }}>
          {result.error && <div style={{ color: T.red || '#EF4444' }}>{result.error}</div>}
          {result.note && <div style={{ color: T.body }}>{result.note}</div>}
          {result.fields && Object.entries(result.fields).map(([f, v]) => (
            <div key={f} style={{ color: T.body }} data-testid={`edm-bulk-preview-${f}`}>
              <strong>{f}</strong> → “{String(v.to)}”: would change {v.wouldChange}, already
              correct on {v.alreadyCorrect}
              {v.examples?.length ? ` — e.g. ${v.examples.slice(0, 3)
                .map((x) => `${x.document_no} (${x.from || 'empty'}→${x.to})`).join(', ')}` : ''}
            </div>
          ))}
          {rows.length > 0 && (
            <div className="max-h-44 overflow-auto" data-testid="edm-bulk-outcomes">
              {rows.map((r, i) => (
                <div key={i} className="flex gap-2 py-0.5">
                  <span className="font-mono" style={{ color: T.primary, minWidth: 130 }}>
                    {r.document_no || r.filename}
                  </span>
                  <span style={{ color: ['CLOSED', 'UPDATED', 'LANDED'].includes(r.outcome)
                    ? (T.green || '#22C55E') : (T.amber || '#E2A93B'), minWidth: 80 }}>
                    {r.outcome}
                  </span>
                  <span style={{ color: T.muted }}>{r.reason || r.note || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
